import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3AssessmentPageState from './useTreatmentPlanV3AssessmentPageState';

import type { AppDispatch } from '../../../../../../store';
import type {
  TreatmentPlanV3AssessmentMethodPayload,
  TreatmentPlanV3AssessmentMethodVisualPayload,
} from '../../../../../../store/treatmentPlanV3';
import type {
  TreatmentPlanV3AssessmentMethodsDependencies,
  UseTreatmentPlanV3AssessmentMethodsResult,
} from './types';

export default function useTreatmentPlanV3AssessmentMethods() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const { methods, templates, isSavingMethods } = useTreatmentPlanV3AssessmentPageState();

  return useTreatmentPlanV3AssessmentMethodsWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    methods,
    templates,
    isSaving: isSavingMethods,
  });
}

export function useTreatmentPlanV3AssessmentMethodsWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  methods,
  templates,
  isSaving,
}: TreatmentPlanV3AssessmentMethodsDependencies): UseTreatmentPlanV3AssessmentMethodsResult {
  const create = useCallback(
    async (data: TreatmentPlanV3AssessmentMethodPayload) => {
      if (!treatmentPlanId) return undefined;
      const result = await dispatch(
        actions.createTreatmentPlanV3AssessmentMethod({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.createTreatmentPlanV3AssessmentMethod.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const update = useCallback(
    async (methodId: number, data: TreatmentPlanV3AssessmentMethodPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3AssessmentMethod({
          treatmentPlanId,
          methodId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3AssessmentMethod.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (methodId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3AssessmentMethod({
          treatmentPlanId,
          methodId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3AssessmentMethod.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const createVisual = useCallback(
    async (
      methodId: number,
      file: File,
      data: TreatmentPlanV3AssessmentMethodVisualPayload,
    ) => {
      if (!treatmentPlanId) return undefined;
      const created = await dispatch(
        actions.createTreatmentPlanV3AssessmentMethodVisuals({
          treatmentPlanId,
          methodId,
          files: [file],
          getAccessTokenSilently,
        }),
      );
      if (!actions.createTreatmentPlanV3AssessmentMethodVisuals.fulfilled.match(created)) {
        return undefined;
      }
      const visual = created.payload[0];
      if (!visual) return undefined;
      const updated = await dispatch(
        actions.updateTreatmentPlanV3AssessmentMethodVisual({
          treatmentPlanId,
          methodId,
          visualId: visual.id,
          data,
          getAccessTokenSilently,
        }),
      );
      if (!actions.updateTreatmentPlanV3AssessmentMethodVisual.fulfilled.match(updated)) {
        await dispatch(
          actions.deleteTreatmentPlanV3AssessmentMethodVisual({
            treatmentPlanId,
            methodId,
            visualId: visual.id,
            getAccessTokenSilently,
          }),
        );
        return undefined;
      }
      return updated.payload.id;
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const updateVisual = useCallback(
    async (
      methodId: number,
      visualId: number,
      data: TreatmentPlanV3AssessmentMethodVisualPayload,
    ) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3AssessmentMethodVisual({
          treatmentPlanId,
          methodId,
          visualId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3AssessmentMethodVisual.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const updateVisualFile = useCallback(
    async (methodId: number, visualId: number, file: File) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3AssessmentMethodVisualFile({
          treatmentPlanId,
          methodId,
          visualId,
          file,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3AssessmentMethodVisualFile.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const removeVisual = useCallback(
    async (methodId: number, visualId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3AssessmentMethodVisual({
          treatmentPlanId,
          methodId,
          visualId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3AssessmentMethodVisual.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    methods,
    templates,
    isSaving,
    create,
    update,
    remove,
    createVisual,
    updateVisual,
    updateVisualFile,
    removeVisual,
  };
}
